import { Switch, Route, Redirect, Router, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";
import { isValidReturnTo } from "@/lib/auth-utils";
import { AppLogo, AppBrandingText } from "@/components/app-logo";
import { NotificationBell } from "@/components/notification-bell";
import { ReportFreshnessBadge } from "@/components/report-freshness-badge";
import { SystemStatusBadge } from "@/components/system-status-badge";
import { BreadcrumbProvider, useBreadcrumbs } from "@/contexts/breadcrumb-context";
import { PanelLeft, Menu } from "lucide-react";
import { Link } from "wouter";

// Pages
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import Reports from "@/pages/reports";
import ReportDetail from "@/pages/report-detail";
import Animals from "@/pages/animals";
import AnimalDetail from "@/pages/animal-detail";
import Analytics from "@/pages/analytics";
import Users from "@/pages/users";
import Settings from "@/pages/settings";
import Account from "@/pages/account";
import Login from "@/pages/login";
import Invite from "@/pages/invite";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import BehaviorReportDetail from "@/pages/behavior-report-detail";
import ReviewQueuePage from "@/pages/review-queue";
import ReviewDetailPage from "@/pages/review-detail";
import Interactions from "@/pages/interactions";
import BehaviorInsights from "@/pages/behavior-insights";
import Summaries from "@/pages/summaries";
import Capacity from "@/pages/capacity";
import PopulationInsights from "@/pages/population-insights";
import AdminGlossary from "@/pages/admin-glossary";
import AdminTours from "@/pages/admin-tours";
import AdminToursAuditPage from "@/pages/admin/tours/audit";
import Logs from "@/pages/logs";
import TaskBoardAdmin from "@/pages/task-board-admin";
import Tasks from "@/pages/tasks";
import Meetings from "@/pages/meetings";
import TaskDetail from "@/pages/task-detail";
import FilesPage from "@/pages/files";
import Onboarding from "@/pages/onboarding";
import MetricsPage from "@/pages/metrics";
import { InsightsLayout } from "@/components/insights/insights-layout";
import { AppBrandingProvider } from "@/contexts/app-branding";
import { TourContextProvider } from "@/components/tours/TourContext";
import { TourManager } from "@/components/tours/TourManager";
import { TourPreviewHighlighter } from "@/components/tours/TourPreviewHighlighter";
import { TourHelpButton } from "@/components/tours/TourHelpButton";
import { useTour } from "@/components/tours/use-tour";

function LayoutBreadcrumbs() {
  const { breadcrumbs } = useBreadcrumbs();

  if (breadcrumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
      {breadcrumbs.map((segment, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return (
          <span key={index} className="flex items-center gap-2">
            {index > 0 && <span className="text-muted-foreground/50" aria-hidden="true">/</span>}
            {segment.href || segment.onClick ? (
              <Link
                href={segment.href || "#"}
                className="hover:text-foreground transition-colors"
                data-testid={`link-breadcrumb-${segment.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={segment.onClick}
              >
                {segment.label}
              </Link>
            ) : isLast ? (
              <span className="text-foreground font-medium">{segment.label}</span>
            ) : (
              <span>{segment.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function AuthenticatedLayoutContent({ children, user, onLogout }: { children: React.ReactNode; user: any; onLogout: () => void }) {
  const { openMobile, setOpenMobile, isMobile, collapsed, setCollapsed } = useSidebar();
  // P23a/G1: pass the literal pathname directly to useTour. The
  // server filters tours by matching each tour's `route_pattern`
  // against this path, so no client-side allowlist is needed.
  const [path] = useLocation();
  const tourState = useTour(path);

  return (
    <TourContextProvider value={tourState}>
      <div className="flex h-screen w-full min-w-0 overflow-hidden">
        <AppSidebar user={user} onLogout={onLogout} />
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-1.5 border-b sticky top-0 z-40 bg-background">
            {isMobile ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpenMobile(true)}
                data-testid="button-mobile-menu"
                aria-label="Open navigation menu"
                className="flex-shrink-0"
              >
                <Menu className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCollapsed(!collapsed)}
                className="flex-shrink-0"
                data-testid="button-toggle-sidebar"
                aria-label="Toggle sidebar"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            )}
            <div className="flex-1 min-w-0">
              <LayoutBreadcrumbs />
            </div>
            <SystemStatusBadge />
            <ReportFreshnessBadge reportTypes={["allPets", "interaction"]} testIdPrefix="global-freshness" />
            <NotificationBell />
            <TourHelpButton />
          </div>
          <main className="flex-1 overflow-auto overflow-x-hidden bg-muted/30">
            {children}
          </main>
          <TourManager />
          <TourPreviewHighlighter />
        </div>
      </div>
    </TourContextProvider>
  );
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const style = {
    "--sidebar-width": "13rem",
    "--sidebar-width-icon": "3rem",
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <BreadcrumbProvider>
      <SidebarProvider style={style as React.CSSProperties}>
        <AuthenticatedLayoutContent user={user} onLogout={handleLogout}>
          {children}
        </AuthenticatedLayoutContent>
      </SidebarProvider>
    </BreadcrumbProvider>
  );
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function LoginRedirectWithReturnTo() {
  const currentPath = window.location.pathname + window.location.search;
  const validated = isValidReturnTo(currentPath) ? currentPath : null;
  const target = validated ? `/login?returnTo=${encodeURIComponent(validated)}` : "/login";
  return <Redirect to={target} />;
}

function AppContent() {
  return (
    <AuthProvider>
      <AppRouter />
      <Toaster />
    </AuthProvider>
  );
}

function BrandedLoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center h-screen gap-6 bg-background animate-in fade-in duration-300" data-testid="loading-screen">
      <div className="animate-pulse">
        <AppLogo size="xl" />
      </div>
      <div className="flex flex-col items-center gap-2">
        <AppBrandingText className="text-2xl" />
        <p className="text-sm text-muted-foreground">Loading your workspace...</p>
      </div>
      <div className="flex gap-1.5 mt-2">
        <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
        <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
        <span className="h-2 w-2 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  );
}

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (!user?.needsOnboarding) {
    return <>{children}</>;
  }

  return <Onboarding />;
}

function EnrollmentGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (!user?.requireEnrollment) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen gap-6 bg-background px-4" data-testid="enrollment-gate">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <Shield className="h-10 w-10 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-enrollment-title">
            Two-Factor Authentication Required
          </h1>
          <p className="text-muted-foreground" data-testid="text-enrollment-description">
            Your organization requires two-factor authentication. Please set up TOTP on your account before continuing.
          </p>
        </div>
        <Button asChild data-testid="button-setup-totp">
          <a href="/account#two-factor">Set Up Two-Factor Authentication</a>
        </Button>
      </div>
    </div>
  );
}

function AppRouter() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <BrandedLoadingScreen />;
  }

  const isAuthenticated = !!user;

  return (
    <Switch>
      {/* Public routes always accessible */}
      <Route path="/login">
        {isAuthenticated ? <Redirect to="/dashboard" /> : <PublicLayout><Login /></PublicLayout>}
      </Route>
      <Route path="/invite">
        <PublicLayout><Invite /></PublicLayout>
      </Route>
      <Route path="/forgot-password">
        <PublicLayout><ForgotPassword /></PublicLayout>
      </Route>
      <Route path="/reset-password">
        <PublicLayout><ResetPassword /></PublicLayout>
      </Route>
      
      {/* Protected routes - require authentication */}
      {isAuthenticated ? (
        <Route>
          <OnboardingGate>
          <AuthenticatedLayout>
            <Switch>
              {/* Account page is always accessible (needed for TOTP enrollment) */}
              <Route path="/account" component={Account} />
              {/* All other routes are gated by enrollment requirement */}
              <Route>
                <EnrollmentGate>
                  <Switch>
                    <Route path="/">
                      <Redirect to="/dashboard" />
                    </Route>
                    <Route path="/dashboard" component={Dashboard} />
                    <Route path="/reports" component={Reports} />
                    <Route path="/reports/review" component={ReviewQueuePage} />
                    <Route path="/reports/review/:reportType/:id" component={ReviewDetailPage} />
                    <Route path="/reports/behavior/:id" component={BehaviorReportDetail} />
                    <Route path="/reports/:reportId" component={ReportDetail} />
                    <Route path="/animals" component={Animals} />
                    <Route path="/animals/:id" component={AnimalDetail} />
                    <Route path="/interactions" component={Interactions} />
                    <Route path="/tasks" component={Tasks} />
                    <Route path="/tasks/:taskId" component={TaskDetail} />
                    <Route path="/meetings" component={Meetings} />
                    <Route path="/meetings/:taskId" component={TaskDetail} />
                    <Route path="/behavior">
                      <InsightsLayout>
                        <BehaviorInsights />
                      </InsightsLayout>
                    </Route>
                    <Route path="/files">
                      <InsightsLayout>
                        <FilesPage />
                      </InsightsLayout>
                    </Route>
                    <Route path="/summaries" component={Summaries} />
                    <Route path="/counts" component={MetricsPage} />
                    <Route path="/population">
                      <InsightsLayout>
                        <PopulationInsights />
                      </InsightsLayout>
                    </Route>
                    <Route path="/capacity">
                      <Redirect to="/population" />
                    </Route>
                    <Route path="/analytics" component={Analytics} />
                    <Route path="/admin/users" component={Users} />
                    <Route path="/admin/settings" component={Settings} />
                    <Route path="/admin/glossary" component={AdminGlossary} />
                    <Route path="/admin/tours/audit" component={AdminToursAuditPage} />
                    <Route path="/admin/tours" component={AdminTours} />
                    <Route path="/admin/tasks" component={TaskBoardAdmin} />
                    <Route path="/logs" component={Logs} />
                    <Route component={NotFound} />
                  </Switch>
                </EnrollmentGate>
              </Route>
            </Switch>
          </AuthenticatedLayout>
          </OnboardingGate>
        </Route>
      ) : (
        <Route>
          <LoginRedirectWithReturnTo />
        </Route>
      )}
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <AppBrandingProvider>
            <Router>
              <AppContent />
            </Router>
          </AppBrandingProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;